/**
 * UseAICommands — the source for the chat input's slash-command palette.
 *
 * Reuses the same `GET /ai/catalog` endpoint the (removed) AICatalogDrawer
 * read, then curates the raw tool metadata down to the _user-facing_
 * capabilities worth surfacing as `/commands`: the descriptor-derived "virtual"
 * cards (Create/List objective, Export ICS 201, …) and the data-source actions
 * (web/file/incident search). The generic create/update/list tools are excluded
 * (they're already removed server-side by CatalogRouter's virtual backing).
 *
 * Each command also carries a `group` (e.g. "Objectives", "ICS Form Exports",
 * "Data Sources") so the palette can render collapsible category sections.
 *
 * Selecting a command prefills `promptTemplate` into the input — it does not
 * invoke a tool directly; the agent still decides and runs through the normal
 * pipeline.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { humanizeFieldName } from '../confirmationFieldConfig';

export interface CatalogTool {
  name: string;
  description: string;
  category: string;
  kind: 'data_source' | 'ics_read' | 'ics_write';
  requiresConfirmation: boolean;
  exampleInvocation?: string;
  imhReference?: string;
  virtualOf?: string;
}

export interface CatalogSkill {
  name: string;
  description: string;
  category: string;
  toolNames: string[];
  examplePrompts: string[];
  entryConditions?: string;
  imhReference?: string;
}

export interface AICatalog {
  tools: CatalogTool[];
  skills: CatalogSkill[];
}

export interface AICommand {
  /** Slash token shown/typed in the input, e.g. "/create-objective". */
  command: string;
  /** Human-readable label, e.g. "Create Objective". */
  label: string;
  description: string;
  category: string;
  /**
   * Top-level palette section: plain `tool`s vs guided `skill`s. Skills are the
   * deepagents subagent workflows (e.g. ICS 201 Gap Analysis).
   */
  kind: 'tool' | 'skill';
  /**
   * Collapsible sub-group heading within the top-level section, e.g.
   * "Objectives" / "ICS Form Exports" / "Data Sources" for tools, "Incident
   * Management" for skills.
   */
  group: string;
  /** Editable text inserted into the input when the command is picked. */
  promptTemplate: string;
  /**
   * Doctrine anchor for skills (e.g. "CG-IMH Ch 4 — Incident Briefing (ICS
   * 201)").
   */
  imhReference?: string;
  /**
   * When the skill applies (e.g. "OP 0 only"), shown so users know why a skill
   * may be unavailable.
   */
  entryConditions?: string;
}

const toSlashToken = (name: string): string => `/${name.replace(/_/g, '-')}`;

/**
 * Curation predicate: a catalog entry becomes a user-facing command when it is
 * a virtual card (`virtualOf` set — the create/list/update entity cards and
 * form exports) or a data-source action. The generic
 * create/update/list_ics_records tools are already filtered out server-side by
 * CatalogRouter, so this surfaces only the per-entity "virtual" cards.
 */
const isUserFacingCommand = (tool: CatalogTool): boolean =>
  tool.kind === 'data_source' || !!tool.virtualOf;

/**
 * Group heading for the palette's collapsible sections. ICS entity cards
 * (create_objective / list_objectives / update_objective) collapse to the
 * entity noun ("Objectives"); form exports group together; data sources group
 * together.
 */
const deriveGroup = (tool: CatalogTool): string => {
  if (tool.kind === 'data_source') {
    return 'Data Sources';
  }
  if (tool.category === 'ics_forms' || tool.virtualOf === 'export_ics_form') {
    return 'ICS Form Exports';
  }
  const entity = tool.name.match(/^(?:create|update|list)_(.+)$/);
  if (entity) {
    const singular = entity[1].replace(/s$/, '');
    return `${humanizeFieldName(singular)}s`;
  }
  return humanizeFieldName(tool.category);
};

const useAICatalog = () =>
  useQuery<AICatalog>({
    queryKey: ['aiCatalog'],
    queryFn: async () => {
      const response = await axios.get(config.apiGateway.aiCatalog);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

/**
 * Sub-group heading for a skill within the "Skills" section. Every current
 * skill is incident-management; when a second skill domain lands, serve the
 * display group from the catalog (alongside `category`) instead of mapping
 * categories client-side.
 */
const SKILL_GROUP = 'Incident Management';

/** Palette rows are one-liners; skills carry long catalog blurbs, so cap them. */
const DESCRIPTION_MAX = 140;
const truncate = (text: string): string =>
  text.length > DESCRIPTION_MAX
    ? `${text.slice(0, DESCRIPTION_MAX - 1).trimEnd()}…`
    : text;

/**
 * Guided skills surface under the "Skills" section. The prefilled prompt names
 * the skill explicitly — the backend's GUIDED SKILLS prompt section instructs
 * the agent to dispatch a skill the user names, so the slash command routes to
 * the skill subagent rather than leaving delegation to model judgment.
 */
const skillToCommand = (skill: CatalogSkill): AICommand => ({
  command: toSlashToken(skill.name),
  label: humanizeFieldName(skill.name),
  description: truncate(skill.description.split('\n')[0]),
  category: skill.category,
  kind: 'skill',
  group: SKILL_GROUP,
  promptTemplate: `Use the ${skill.name} skill: ${
    skill.examplePrompts?.[0] ?? humanizeFieldName(skill.name)
  }`,
  imhReference: skill.imhReference,
  entryConditions: skill.entryConditions,
});

// Top-level palette order: Skills first (they're the discovery target), then Tools.
const KIND_ORDER: Record<AICommand['kind'], number> = { skill: 0, tool: 1 };

/**
 * Pure catalog → command-list transform (exported for tests). Sorted by
 * top-level kind (Skills first), then sub-group, then label, so the palette's
 * two-level builder receives a stable, pre-ordered list.
 *
 * Skills are omitted in read-only 'ask' mode: the backend skips the entire
 * skill layer there (no subagents, no GUIDED SKILLS menu), so offering a skill
 * command would prefill a request the agent silently can't honor.
 */
export const buildCommands = (data?: AICatalog, mode?: string): AICommand[] => {
  const toolCommands: AICommand[] = (data?.tools ?? [])
    .filter(isUserFacingCommand)
    .map((tool) => ({
      command: toSlashToken(tool.name),
      label: humanizeFieldName(tool.name),
      description: tool.description.split('\n')[0],
      category: tool.category,
      kind: 'tool',
      group: deriveGroup(tool),
      promptTemplate: tool.exampleInvocation ?? humanizeFieldName(tool.name),
    }));

  const skillCommands =
    mode === 'ask' ? [] : (data?.skills ?? []).map(skillToCommand);

  return [...skillCommands, ...toolCommands].sort(
    (a, b) =>
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
      a.group.localeCompare(b.group) ||
      a.label.localeCompare(b.label)
  );
};

/**
 * @param mode The chat's agent mode ('ask' | 'agent_auto' | 'agent_confirm');
 *   skills are hidden in 'ask'.
 */
export const useAICommands = (mode?: string): AICommand[] => {
  const { data } = useAICatalog();

  return useMemo(() => buildCommands(data, mode), [data, mode]);
};
